import React, { useState, useEffect } from 'react';
import StatusTracker from '../pages/StatusTracker';
import useDeploymentSocket from '../hooks/useDeploymentSocket';
import './DeployForm.css';
import { useToast } from "../components/Toast";
import { useNavigate } from 'react-router-dom';
import { Rocket, BarChart2, Copy, ExternalLink, ChevronDown, Settings } from "lucide-react";

const GKE_STAGES = [
  'Preparing Deployment',
  'Provisioning Cloud Infrastructure',
  'Verifying Domain & DNS',
  'Installing Application',
  'Fetching Access Credentials',
];

const KUBEADM_STAGES = [
  'Preparing Deployment',
  'Running Cluster Manager',
  'Setting up HTTPS',
  'Installing Application',
  'Fetching Access Credentials',
];

const KUBEADM_STAGES_NO_HTTPS = [
  'Preparing Deployment',
  'Running Cluster Manager',
  'Setting up HTTPS',
  'Installing Application',
  'Fetching Access Credentials',
];

const DeployForm = () => {
  const {
    currentStage, errorStage, statusMessages, logEndRef,
    isActive, startDeployment,
  } = useDeploymentSocket();

  const [activeTab, setActiveTab] = useState(isActive ? 'status' : 'deploy');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [regions, setRegions] = useState([]);
  const [regionDropdownOpen, setRegionDropdownOpen] = useState(false);
  const [showPolicyDropdown, setShowPolicyDropdown] = useState(false);
  const [errors, setErrors] = useState({ customerName: "", nodes: "", domain: "", rc: "" });

  const { showToast } = useToast();
  const navigate = useNavigate();
  const SKIP_AUTH = process.env.REACT_APP_SKIP_AUTH === 'true';
  const backend_url = process.env.REACT_APP_BACKEND_URL;

  const [formData, setFormData] = useState({
    customerName: '',
    nodes: '',
    domain: '',
    dockerSecrets: '',
    publicKey: '',
    publicIP: '',
    rc: '',
    policy: 'US',
    region: 'us-east1',
    deploymentType: 'gke',
    cloud: 'gcp',
    zone: '',
    projectId: '',
    osType: 'ubuntu',
    hasGateway: true,
    installApp: true,
    enableHttps: true,
    timeout: 1800,
  });

  const isKubeadm = formData.deploymentType === 'kubeadm_hub';

  // For the status tab during resume, read stored deployment type and HTTPS flag
  const activeDeploymentType = localStorage.getItem("activeDeploymentType") || formData.deploymentType;
  const activeEnableHttps = localStorage.getItem("activeEnableHttps") === "1" || formData.enableHttps;
  const statusIsKubeadm = activeDeploymentType === 'kubeadm_hub';
  const kubeadmStageList = activeEnableHttps ? KUBEADM_STAGES : KUBEADM_STAGES_NO_HTTPS;
  const kubeadmStages = formData.installApp
    ? kubeadmStageList
    : kubeadmStageList.slice(0, 2);
  const currentStages = statusIsKubeadm ? kubeadmStages : GKE_STAGES;
  const totalStages = currentStages.length;

  // Fetch regions
  useEffect(() => {
    async function fetchRegions() {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${backend_url}/api/deploy/regions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.ok) setRegions(data.regions);
      } catch (err) {
        console.error("Failed to load regions", err);
      }
    }
    fetchRegions();
  }, [backend_url]);

  const regionsGrouped = regions.reduce((acc, region) => {
    if (!acc[region.continent]) acc[region.continent] = [];
    acc[region.continent].push(region);
    return acc;
  }, {});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newErrors = { ...errors };

    if (name === "customerName") {
      const regex = /^[a-z][a-zA-Z0-9_-]{0,19}$/;
      newErrors.customerName = regex.test(value) ? "" : "Must start with lowercase, no spaces, max 20 characters.";
    }
    if (name === "nodes") {
      const num = Number(value);
      if (isKubeadm) {
        newErrors.nodes = !value ? "Nodes required." : num < 1 || num > 10 ? "1-10 nodes allowed." : "";
      } else {
        newErrors.nodes = !value ? "Nodes required." : num !== 3 && num !== 4 ? "Only 3 or 4 nodes allowed." : "";
      }
    }
    if (name === "domain") {
      // Domain is optional for kubeadm when HTTPS is disabled
      const isDomainRequired = !isKubeadm || formData.enableHttps;
      if (!value && !isDomainRequired) {
        newErrors.domain = "";
      } else {
        const domainRegex = /^[a-z][a-zA-Z0-9-\.]*\.(lightbeam-ai\.com|lightbeamsecurity\.com)$/;
        newErrors.domain = domainRegex.test(value) ? "" : "Enter a valid domain (e.g., customerName.lightbeam-ai.com)";
      }
    }
    if (name === "rc") {
      const rcRegex = /^release-v\d+\.\d+\.\d+-\d{4}-\d{2}-\d{2}-\d+$/;
      newErrors.rc = rcRegex.test(value) ? "" : "Format: release-vX.X.X-YYYY-MM-DD-NN";
    }

    setErrors(newErrors);
    const val = type === 'checkbox' ? checked : value;
    setFormData({ ...formData, [name]: val });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.customerName.length > 20) {
      showToast('Customer name must not exceed 20 characters.', "Fail");
      return;
    }

    let token = localStorage.getItem('authToken');
    if (SKIP_AUTH) token = 'dev-token';
    else if (!token) {
      showToast('You are not authenticated', "Fail");
      return;
    }

    try {
      const response = await fetch(`${backend_url}/api/deploy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Deployment failed');
      }

      localStorage.setItem("activeDeploymentType", formData.deploymentType);
      localStorage.setItem("activeEnableHttps", formData.enableHttps ? "1" : "0");
      startDeployment(formData.customerName, totalStages);
      setActiveTab('status');

    } catch (error) {
      showToast(error.message || 'Failed to submit form', "Fail");
    }
  };

  return (
    <div className="deploy-container">
      <div className="tabs-header">
        <div
          className={`tab ${activeTab === 'deploy' ? 'active' : ''}`}
          onClick={() => setActiveTab('deploy')}
        >
          <Rocket size={16} className="tab-icon" />
          Launch
        </div>
        <div
          className={`tab ${activeTab === 'status' ? 'active' : ''}`}
          onClick={() => setActiveTab('status')}
        >
          <BarChart2 size={16} className="tab-icon" />
          Status
        </div>
      </div>

      <div className="tab-content">
        {activeTab === 'deploy' && (
          <form onSubmit={handleSubmit} className="form">
            <h2>Launch Cluster</h2>
            <p className="form-subtitle">Configure and deploy a new cluster instance</p>

            {/* ── Cloud & Deployment Type ── */}
            <div className="form-section">
              <div className="section-header">Deployment Type</div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Cloud Provider</label>
                  <div className="selector-pills">
                    {[{ value: 'gcp', label: 'GCP' }].map(opt => (
                      <div
                        key={opt.value}
                        className={`pill ${formData.cloud === opt.value ? 'pill-active' : ''}`}
                        onClick={() => setFormData({ ...formData, cloud: opt.value })}
                      >
                        {opt.label}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Cluster Type</label>
                  <div className="selector-pills">
                    {[
                      { value: 'gke', label: 'Managed (GKE)' },
                      { value: 'kubeadm_hub', label: 'Kubeadm Hub' },
                    ].map(opt => (
                      <div
                        key={opt.value}
                        className={`pill ${formData.deploymentType === opt.value ? 'pill-active' : ''}`}
                        onClick={() => setFormData({ ...formData, deploymentType: opt.value })}
                      >
                        {opt.label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Cluster Configuration ── */}
            <div className="form-section">
              <div className="section-header">Cluster Configuration</div>
              <div className="form-grid">
                <div className="form-group wide">
                  <label className="field-label">
                    Customer Name
                    <span className="info-text">max 20 chars, lowercase start, no spaces</span>
                  </label>
                  <input
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleChange}
                    required
                    maxLength={20}
                    pattern="^[a-z][a-zA-Z0-9_-]{0,19}$"
                    placeholder="e.g. acmecorp"
                  />
                  {errors.customerName && <div className="error-popup">{errors.customerName}</div>}
                </div>

                <div className="form-group">
                  <label>Nodes</label>
                  <input
                    type="number"
                    name="nodes"
                    value={formData.nodes}
                    onChange={handleChange}
                    required
                    min={isKubeadm ? 1 : 3}
                    max={isKubeadm ? 10 : 4}
                    placeholder={isKubeadm ? "1-10" : "3 or 4"}
                  />
                  {errors.nodes && <div className="error-popup">{errors.nodes}</div>}
                </div>

                <div className="form-group">
                  <label>Domain {isKubeadm && !formData.enableHttps && <span className="info-text">(optional for HTTP)</span>}</label>
                  <input
                    type="text"
                    name="domain"
                    value={formData.domain}
                    placeholder="e.g. acmecorp.lightbeam-ai.com"
                    onChange={handleChange}
                    required={!isKubeadm || formData.enableHttps}
                  />
                  {errors.domain && <div className="error-popup">{errors.domain}</div>}
                </div>

                <div className="form-group">
                  <label>Region</label>
                  <div
                    className="region-dropdown-box"
                    onClick={() => setRegionDropdownOpen(!regionDropdownOpen)}
                  >
                    {formData.region}
                    <ChevronDown size={16} className="dropdown-chevron" />
                  </div>
                  {regionDropdownOpen && (
                    <div className="region-dropdown-list">
                      {Object.keys(regionsGrouped).map(continent => (
                        <div key={continent} className="region-group">
                          <div className="region-group-title">{continent}</div>
                          {regionsGrouped[continent].map(region => (
                            <div
                              key={region.id}
                              className="region-option"
                              onClick={() => {
                                setFormData({ ...formData, region: region.region_code });
                                setRegionDropdownOpen(false);
                              }}
                            >
                              {region.region_code}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Release Configuration ── */}
            <div className="form-section">
              <div className="section-header">Release Configuration</div>
              <div className="form-grid">
                <div className="form-group">
                  <div className="rc-label">
                    <label htmlFor="rc">RC Version</label>
                    <div className="tooltip-wrapper">
                      <span className="info-icon">i</span>
                      <div className="tooltip-box">
                        <strong>View documentation for Stable RC versions.</strong>
                        <br />
                        <a
                          href="https://lightbeamai.atlassian.net/wiki/spaces/PM/pages/1770225668/Product+Releases"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          LightBeam RC Release Notes
                        </a>
                      </div>
                    </div>
                  </div>
                  <input
                    type="text"
                    name="rc"
                    id="rc"
                    value={formData.rc}
                    placeholder="release-v3.3.0-2025-10-25-30"
                    onChange={handleChange}
                    required
                  />
                  {errors.rc && <div className="error-popup">{errors.rc}</div>}
                </div>

                <div className="form-group">
                  <label>Compliance Policy</label>
                  <div
                    className="policy-dropdown-box"
                    onClick={() => setShowPolicyDropdown(!showPolicyDropdown)}
                  >
                    {formData.policy || "Select Policy"}
                    <ChevronDown size={16} className="dropdown-chevron" />
                  </div>
                  {showPolicyDropdown && (
                    <div className="policy-dropdown-list">
                      {["US", "CANADA", "NONE"].map(opt => (
                        <div
                          key={opt}
                          className="policy-option"
                          onClick={() => {
                            setFormData({ ...formData, policy: opt });
                            setShowPolicyDropdown(false);
                          }}
                        >
                          {opt}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {isKubeadm && (
                  <div className="form-group">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        name="enableHttps"
                        checked={formData.enableHttps}
                        onChange={handleChange}
                        className="toggle-checkbox"
                      />
                      <span className="toggle-switch" />
                      Enable HTTPS
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* ── Kubeadm: Infrastructure Settings ── */}
            {isKubeadm && (
              <div className="form-section">
                <div className="section-header">Infrastructure Settings</div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Zone</label>
                    <input
                      type="text"
                      name="zone"
                      value={formData.zone}
                      onChange={handleChange}
                      placeholder="e.g. us-east1-b"
                    />
                  </div>

                  <div className="form-group">
                    <label>OS Type</label>
                    <div className="selector-pills">
                      {[
                        { value: 'ubuntu', label: 'Ubuntu' },
                        { value: 'rhel', label: 'RHEL' },
                      ].map(opt => (
                        <div
                          key={opt.value}
                          className={`pill ${formData.osType === opt.value ? 'pill-active' : ''}`}
                          onClick={() => setFormData({ ...formData, osType: opt.value })}
                        >
                          {opt.label}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Gateway toggle hidden — has_gateway=false is not supported by current Terraform code */}

                  <div className="form-group">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        name="installApp"
                        checked={formData.installApp}
                        onChange={handleChange}
                        className="toggle-checkbox"
                      />
                      <span className="toggle-switch" />
                      Install LightBeam App
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* ── Advanced Options ── */}
            <div
              className="advanced-toggle"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              <Settings size={16} className="advanced-toggle-icon" />
              <span>Advanced Options</span>
              <ChevronDown size={16} className={`advanced-chevron ${showAdvanced ? 'open' : ''}`} />
            </div>

            {showAdvanced && (
              <div className="form-section advanced-section">
                <div className="form-grid">
                  {isKubeadm && (
                    <div className="form-group">
                      <label className="field-label">
                        GCP Project ID
                        <span className="info-text">from environment config</span>
                      </label>
                      <input
                        type="text"
                        name="projectId"
                        value={formData.projectId}
                        readOnly
                        className="input-readonly"
                        placeholder="Set via backend config"
                      />
                    </div>
                  )}

                  {!isKubeadm && (
                    <div className="form-group">
                      <label>Docker Secrets <span className="optional">(Optional)</span></label>
                      <input name="dockerSecrets" value={formData.dockerSecrets} onChange={handleChange} />
                    </div>
                  )}

                </div>
              </div>
            )}

            <button type="submit" className="launch-btn">
              <Rocket size={18} />
              Launch Cluster
            </button>
          </form>
        )}

        {activeTab === 'status' && (
          <div className="status-layout">
            <div className="status-tracker-panel">
              <StatusTracker
                currentStage={currentStage}
                errorStage={errorStage}
                stages={currentStages}
              />
            </div>
            <div className="status-log-panel">
              <div className="log-header">Deployment Logs</div>
              <div className="status-log">
                {statusMessages.map((msg, i) => (
                  <div key={i} className={`log-line ${msg.startsWith('Error:') ? 'log-error' : ''}`}>
                    {msg}
                  </div>
                ))}
                <div ref={logEndRef} />
              </div>

              {!statusIsKubeadm && errorStage === 2 && (
                <div className="dns-guidance">
                  <div className="dns-guidance-title">Action Required: Domain Mapping</div>
                  <p>
                    The DNS configuration could not be verified. Please raise an
                    <strong> Infra JIRA ticket</strong> requesting domain name mapping with the following details:
                  </p>
                  <div className="dns-details">
                    <div className="dns-detail-row">
                      <span className="dns-detail-label">Domain</span>
                      <span className="dns-detail-value">
                        {formData.domain}
                        <Copy
                          size={14}
                          className="dns-copy-icon"
                          onClick={() => {
                            navigator.clipboard.writeText(formData.domain);
                            showToast("Domain copied!", "Success");
                          }}
                        />
                      </span>
                    </div>
                    <div className="dns-detail-row">
                      <span className="dns-detail-label">Public IP</span>
                      <span className="dns-detail-value">
                        {formData.publicIP || "Assigned by infrastructure"}
                        {formData.publicIP && (
                          <Copy
                            size={14}
                            className="dns-copy-icon"
                            onClick={() => {
                              navigator.clipboard.writeText(formData.publicIP);
                              showToast("Public IP copied!", "Success");
                            }}
                          />
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="dns-next-steps">
                    <strong>Next steps:</strong>
                    <ol>
                      <li>Create an Infra JIRA ticket with the domain and public IP above</li>
                      <li>Wait for the DNS mapping to be completed by the infra team</li>
                      <li>
                        Go to{' '}
                        <span className="dns-link" onClick={() => navigate('/dashboard/list')}>
                          Cluster Overview <ExternalLink size={13} />
                        </span>
                        {' '}and use the action menu to <strong>Retry Stage</strong>
                      </li>
                    </ol>
                  </div>
                </div>
              )}

              {statusIsKubeadm && activeEnableHttps && errorStage === 2 && (
                <div className="dns-guidance">
                  <div className="dns-guidance-title">Action Required: Domain Mapping</div>
                  <p>
                    HTTPS is enabled but DNS has not been configured yet. Please map your domain
                    to the cluster's public IP address:
                  </p>
                  <div className="dns-details">
                    <div className="dns-detail-row">
                      <span className="dns-detail-label">Domain</span>
                      <span className="dns-detail-value">
                        {formData.domain}
                        <Copy
                          size={14}
                          className="dns-copy-icon"
                          onClick={() => {
                            navigator.clipboard.writeText(formData.domain);
                            showToast("Domain copied!", "Success");
                          }}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="dns-next-steps">
                    <strong>Next steps:</strong>
                    <ol>
                      <li>Create a DNS A record mapping your domain to the cluster's public IP</li>
                      <li>Wait for DNS propagation (verify with <code>nslookup {formData.domain}</code>)</li>
                      <li>
                        Go to{' '}
                        <span className="dns-link" onClick={() => navigate('/dashboard/list')}>
                          Cluster Overview <ExternalLink size={13} />
                        </span>
                        {' '}and use the action menu to <strong>Retry Stage</strong>
                      </li>
                    </ol>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeployForm;
